import { NgClass } from '@angular/common';
import { Component, effect, inject, Injector, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CodeList } from '@common/classes/codelist';
import { AppLinkComponent } from '@common/components/app-link.component';
import { InputErrorComponent } from '@common/components/input/input-error/input-error.component';
import { InputLabelComponent } from '@common/components/input/input-label/input-label.component';
import { defaultSelectLabel } from '@common/models/util';
import { CodelistPipe } from '@common/pipes/codelist.pipe';
import { environment } from '@environments/environment';
import { tapSuccessResult } from '@ngneat/query';
import { SubscribeDirective } from '@ngneat/subscribe';
import { TranslatePipe } from '@ngx-translate/core';
import {
    ComboBoxComponent,
    CustomMessagesComponent,
    ItemTemplateDirective,
    MultiSelectComponent
} from '@progress/kendo-angular-dropdowns';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { debounce } from 'lodash';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { BaseInputComponent } from '../base-input/base-input.component';
import { CodelistInputConfig } from '../input.type';

/**
 * Codelist input component.
 * @param codelist - The codelist name.
 * @param isMultiple - Whether the input is multiple.
 * @param textField - The text field of the codelist.
 * @param valueField - The value field of the codelist.
 * @param popupSettings - The popup settings of the dropdown.
 * @param formatLabel - The function to format the label.
 * @param customFilter - The custom filter function.
 */
@Component({
    selector: 'app-codelist-input',
    templateUrl: './codelist-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: CodelistInputComponent }],
    imports: [
        InputLabelComponent,
        MultiSelectComponent,
        SubscribeDirective,
        NgClass,
        TranslatePipe,
        FormsModule,
        ItemTemplateDirective,
        CustomMessagesComponent,
        ComboBoxComponent,
        InputErrorComponent,
        TooltipDirective,
        AppLinkComponent
    ]
})
export class CodelistInputComponent
    extends BaseInputComponent<string | string[] | number | number[]>
    implements OnInit, OnDestroy
{
    @Input({ required: true }) codelist: CodelistInputConfig['codelist'];
    @Input() isMultiple: CodelistInputConfig['isMultiple'];
    @Input() textField: CodelistInputConfig['textField'] = 'label';
    @Input() valueField: CodelistInputConfig['valueField'] = 'value';
    @Input() popupSettings: CodelistInputConfig['popupSettings'] =
        environment.settings.appControl.dropdown.popupSettings;
    @Input() omittedCodelistIds: CodelistInputConfig['omittedCodelistIds'];
    @Input() customFilter: CodelistInputConfig['customFilter'];
    query$;
    options = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    filteredOptions = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    readonly codelistTake = environment.settings.appControl.codelist.take;
    formatedSelectedOptions = '';
    queryCodelistDebounced = debounce((filter) => this.queryCodelist(filter), 500);
    private codelistPipe = inject(CodelistPipe);
    private injector = inject(Injector);
    private destroy$ = new Subject<boolean>();

    constructor() {
        super();
        effect(async () => (this.formatedSelectedOptions = await this.formatSelectedOptions(this.value())));
    }

    @Input() formatLabel: CodelistInputConfig['formatLabel'] = (item: CodeList) => defaultSelectLabel(item);

    ngOnInit(): void {
        this.queryCodelist('');
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    queryCodelist(filter) {
        this.query$ = this.queryService.getCodelistQuery({
            name: this.codelist,
            filter,
            selectedIds: this.value() ? [this.value()].flat() : null,
            omittedIds: this.omittedCodelistIds,
            injector: this.injector
        }).result$;
        this.query$
            .pipe(
                tapSuccessResult((data: []) => {
                    this.options.set(
                        data.map((x: any) => ({
                            label: this.formatLabel?.(x) || defaultSelectLabel(x),
                            value: x.id,
                            name: x.name
                        }))
                    );

                    const currentValue = this.isMultiple ? this.value()?.[0] : this.value();
                    if (this.customFilter) {
                        this.filteredOptions.set(this.options().filter((x: any) => this.customFilter(x, filter)));
                        return;
                    }
                    this.filteredOptions.set(
                        this.options().filter((x, i) => x.value === currentValue || i < this.codelistTake + 1)
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    async formatSelectedOptions(selected: string | string[] | number | number[]): Promise<string> {
        if (!this.options() || !selected) return null;

        if (this.isMultiple) {
            if (!Array.isArray(selected) || selected.length === 0) {
                return null;
            }
            return await Promise.all(
                selected.map(
                    async (item) => await lastValueFrom(this.codelistPipe.transform(item, this.codelist, false))
                )
            ).then((res) => res.join(', '));
        } else {
            if (Array.isArray(selected)) return null;
            return await lastValueFrom(this.codelistPipe.transform(selected, this.codelist, false));
        }
    }
}
